import React, { useState } from "react";
import api from "@/lib/api";
import { useLiveQuery, TAGS } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TicketIcon, MagnifyingGlass, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function CustomerCoupons() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const validate = async (e) => {
    e?.preventDefault();
    const c = code.trim().toUpperCase();
    if (!c) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/coupons/validate/${encodeURIComponent(c)}`);
      setResult({ ok: true, ...data });
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || "Geçersiz kod" });
    } finally { setBusy(false); }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(result.code);
      toast.success("Kod kopyalandı");
    } catch { toast.error("Kopyalanamadı"); }
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Promosyon</div>
        <h1 className="text-3xl font-bold tracking-tight">Kupon Kodları</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Kupon kodunuz varsa çıkışta operatöre söyleyin. Aşağıdaki alandan
          kodunuzun geçerliliğini önceden kontrol edebilirsiniz.
        </p>
      </div>

      <Card className="rounded-sm border-border">
        <CardContent className="p-5 space-y-4">
          <form onSubmit={validate} className="flex gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="rounded-sm font-mono uppercase h-11 text-base"
              placeholder="KUPON KODU"
              data-testid="cust-coupons-input"
              autoFocus
            />
            <Button
              type="submit"
              disabled={busy || !code.trim()}
              data-testid="cust-coupons-check-btn"
              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-semibold h-11 px-5"
            >
              <MagnifyingGlass size={14} /> Kontrol Et
            </Button>
          </form>

          {result?.ok && (
            <div className="p-4 border-2 border-emerald-400 bg-emerald-50 rounded-sm space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle size={24} weight="fill" className="text-emerald-600" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">
                    Geçerli
                  </div>
                  <div className="font-mono font-bold text-lg" data-testid="cust-coupon-code-result">
                    {result.code}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">İndirim</div>
                  <div className="font-mono font-bold">
                    {result.kind === "percent" ? `%${result.value}` : `₺${result.value}`}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">Kalan Kullanım</div>
                  <div className="font-mono">
                    {result.remaining_uses === null ? "∞ sınırsız" : result.remaining_uses}
                  </div>
                </div>
              </div>
              {result.description && (
                <div className="text-xs text-muted-foreground">{result.description}</div>
              )}
              <Button
                onClick={copyToClipboard}
                variant="outline"
                className="w-full rounded-sm uppercase tracking-widest text-xs font-semibold"
                data-testid="cust-coupon-copy-btn"
              >
                <TicketIcon size={14} /> Kodu Kopyala
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Çıkışta bu kodu operatöre söyleyin, ücret üzerine indirim uygulanacak.
              </p>
            </div>
          )}

          {result && !result.ok && (
            <div className="p-4 border border-destructive/30 bg-destructive/5 rounded-sm flex items-center gap-2">
              <XCircle size={20} weight="fill" className="text-destructive" />
              <span className="text-sm" data-testid="cust-coupon-error">{result.error}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-sm border-dashed border-border">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Nasıl Kullanılır?</div>
          <ol className="text-sm space-y-1.5 list-decimal ml-4">
            <li>Bir kampanya, sosyal medya vb. kanaldan kupon kodu edinin.</li>
            <li>Kodu bu sayfada doğrulayın.</li>
            <li>Otoparktan çıkarken kodu <b>operatöre</b> söyleyin.</li>
            <li>Operatör kodu sisteme girer, ücret üzerinde otomatik indirim uygulanır.</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
